/**
 * Star Metadata Utilities
 *
 * Handles data transformation between backend and frontend formats
 * to maintain compatibility as backend data structures evolve.
 *
 * Backend Team Compatibility Solution:
 * - Backend normalization converts strings to arrays
 * - Frontend needs strings for form compatibility
 * - This utility provides safe conversion with backward compatibility
 *
 * 🎯 FRONTEND-BACKEND SHARED VOCABULARY CONTRACT
 * - Unified vocabulary mappings for both teams
 * - Cultural context detection for ethnicity/musical style
 * - Hair color normalization for edge cases like "multi-color"
 * - Client-side inheritance fallback when backend inheritance fails
 */

// =============================================================================
// 🎯 SHARED VOCABULARY CONTRACT (Frontend-Backend Coordination)
// =============================================================================

/**
 * Hair Color Vocabulary Mappings
 * Maps various user inputs and AI-generated values to standardized options
 * SHARED WITH BACKEND TEAM - Keep in sync!
 */
export const HAIR_COLOR_VOCABULARY = {
  // Standard colors (exact dropdown matches)
  black: 'Black',
  brown: 'Brown',
  blonde: 'Blonde',
  blond: 'Blonde', // Common misspelling
  red: 'Red',
  auburn: 'Auburn',
  gray: 'Gray',
  grey: 'Gray', // UK spelling
  white: 'White',
  silver: 'Gray', // Common variation

  // Fantasy/dyed colors
  blue: 'Blue',
  pink: 'Pink',
  purple: 'Purple',
  green: 'Green',
  orange: 'Orange',
  yellow: 'Yellow',

  // Multi-color variants (the main issue we're fixing)
  'multi-color': 'Other',
  multicolor: 'Other',
  'multi color': 'Other',
  colorful: 'Other',
  rainbow: 'Other',
  mixed: 'Other',
  streaks: 'Other',
  highlights: 'Other',
  ombre: 'Other',
  gradient: 'Other',
  'two-tone': 'Other',
  'two tone': 'Other',
  'dip-dyed': 'Other',
  'dip dyed': 'Other',

  // Common descriptive terms
  dark: 'Black',
  light: 'Blonde',
  fair: 'Blonde',
  'dirty blonde': 'Blonde',
  'strawberry blonde': 'Blonde',
  platinum: 'Blonde',
  chestnut: 'Brown',
  brunette: 'Brown',
  raven: 'Black',
  'jet black': 'Black',
} as const;

/**
 * Cultural Context to Ethnicity Mapping
 * Maps cultural references to ethnicity values
 * SHARED WITH BACKEND TEAM - Keep in sync!
 */
export const CULTURAL_ETHNICITY_MAP = {
  // Asian cultures
  'k-pop': 'Korean',
  kpop: 'Korean',
  korean: 'Korean',
  'j-pop': 'Japanese',
  jpop: 'Japanese',
  japanese: 'Japanese',
  anime: 'Japanese',
  manga: 'Japanese',
  chinese: 'Chinese',
  thai: 'Thai',
  vietnamese: 'Vietnamese',
  filipino: 'Filipino',
  indian: 'Indian',
  bollywood: 'Indian',

  // European cultures
  spanish: 'Spanish',
  flamenco: 'Spanish',
  italian: 'Italian',
  french: 'French',
  german: 'German',
  russian: 'Russian',
  british: 'British',
  irish: 'Irish',
  scottish: 'Scottish',
  nordic: 'Scandinavian',
  scandinavian: 'Scandinavian',

  // American cultures
  country: 'American',
  american: 'American',
  southern: 'American',
  'tex-mex': 'Mexican',
  mexican: 'Mexican',
  latin: 'Latino',
  latino: 'Latino',
  hispanic: 'Hispanic',

  // African cultures
  afrobeat: 'African',
  african: 'African',
  reggae: 'Caribbean',
  caribbean: 'Caribbean',
  jamaican: 'Jamaican',

  // Middle Eastern cultures
  'middle eastern': 'Middle Eastern',
  arabic: 'Arabic',
  persian: 'Persian',
  turkish: 'Turkish',
} as const;

/**
 * Musical Style Context Detection
 * Maps cultural and genre terms to musical styles
 * SHARED WITH BACKEND TEAM - Keep in sync!
 */
export const MUSICAL_STYLE_CONTEXT_MAP = {
  // K-Pop and Asian music
  'k-pop': ['K-Pop', 'Pop'],
  kpop: ['K-Pop', 'Pop'],
  'j-pop': ['J-Pop', 'Pop'],
  jpop: ['J-Pop', 'Pop'],

  // Western genres
  pop: ['Pop'],
  rock: ['Rock'],
  'hip-hop': ['Hip-Hop'],
  'hip hop': ['Hip-Hop'],
  rap: ['Hip-Hop'],
  jazz: ['Jazz'],
  blues: ['Blues'],
  country: ['Country'],
  folk: ['Folk'],
  classical: ['Classical'],
  opera: ['Classical', 'Opera'],

  // Electronic and dance
  electronic: ['Electronic'],
  edm: ['Electronic', 'Dance'],
  house: ['Electronic', 'Dance'],
  techno: ['Electronic'],
  disco: ['Disco', 'Dance'],
  dance: ['Dance'],

  // Alternative and indie
  alternative: ['Alternative'],
  indie: ['Indie'],
  grunge: ['Alternative', 'Rock'],
  punk: ['Punk', 'Rock'],

  // World music
  reggae: ['Reggae'],
  latin: ['Latin'],
  salsa: ['Latin', 'Dance'],
  flamenco: ['World', 'Spanish'],
  bollywood: ['World', 'Indian'],
  afrobeat: ['World', 'African'],
} as const;

export interface StarMetadata {
  // Basic information
  starName?: string | string[];
  archetype?: string | string[];
  energyLevel?: string | string[];
  gender?: string | string[];

  // Hair style information
  hairStyle?: string | string[] | { style?: string };
  hairColor?: string | string[];
  hairLength?: string | string[];

  // Makeup information
  makeupStyle?: string | string[] | { type?: string };
  makeupType?: string | string[];
  colorPalette?: string | string[];

  // Outfit information
  outfitStyle?: string | string[];

  // Variant information
  assetType?: string;
  baseStarId?: string;
  variantName?: string;
  variantDescription?: string;

  // Legacy fields for backward compatibility
  celebrityName?: string;
  celebrityType?: string;
  energyProfile?: string;
  hairStyleDetails?: { style?: string; color?: string; length?: string };
  makeupStyleDetails?: { type?: string; palette?: string };

  // AI metadata
  compatibilityTags?: string[];
  performanceContext?: string[];

  [key: string]: any; // Allow additional fields
}

/**
 * Safely extracts a string value from various data types
 * Handles: strings, arrays, objects with nested properties
 */
export const extractStringValue = (value: any): string => {
  // Already a string - return as-is
  if (typeof value === 'string') {
    return value;
  }

  // Array - take first non-empty element
  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) {
        return item;
      }
    }
    return '';
  }

  // Object with nested properties (legacy format)
  if (value && typeof value === 'object') {
    console.log(
      '🔍 [EXTRACT] Object value received:',
      value,
      'Keys:',
      Object.keys(value)
    );

    // Special handling for hairStyle objects that might have different structures
    if (value.style && typeof value.style === 'string') {
      console.log('🔍 [EXTRACT] Found style property:', value.style);
      return value.style;
    }

    // CRITICAL FIX: Handle array-like objects where string is stored as indexed characters
    // Backend sometimes converts "curly" → {0: "c", 1: "u", 2: "r", 3: "l", 4: "y", length: 5}
    const keys = Object.keys(value);
    const numericKeys = keys
      .filter(key => /^\d+$/.test(key))
      .sort((a, b) => parseInt(a) - parseInt(b));

    if (numericKeys.length > 0 && value.hasOwnProperty('length')) {
      // Reconstruct string from indexed character properties
      const reconstructed = numericKeys.map(key => value[key]).join('');
      console.log(
        '🔧 [EXTRACT] Reconstructed from array-like object:',
        reconstructed
      );
      if (reconstructed && reconstructed.trim()) {
        return reconstructed;
      }
    }

    // Try other common property names that might contain string values
    const stringProperties = Object.keys(value).filter(
      key => typeof value[key] === 'string' && !/^\d+$/.test(key)
    );
    console.log('🔍 [EXTRACT] String properties found:', stringProperties);

    if (stringProperties.length > 0) {
      const result = value[stringProperties[0]];
      console.log(
        '🔍 [EXTRACT] Using first string property:',
        stringProperties[0],
        '=',
        result
      );
      return result;
    }

    // Fallback to original logic
    const result =
      value.style ||
      value.type ||
      value.palette ||
      value.color ||
      value.length ||
      '';
    console.log('🔍 [EXTRACT] Fallback extraction result:', result);
    return typeof result === 'string' ? result : '';
  }

  // Fallback to empty string
  return '';
};

/**
 * Normalizes color palette values to match dropdown options
 * Converts AI-generated values to form-compatible values
 */
const normalizeColorPaletteValue = (value: string): string => {
  if (!value || value.trim() === '') return '';

  const normalized = value.toLowerCase().replace(/[-_]/g, ' ').trim();

  // Map common AI-generated values to dropdown values (match exact capitalization)
  const mappings: { [key: string]: string } = {
    'bold colors': 'Bold Colors', // Fix: AI generates lowercase, dropdown expects title case
    'bold-colors': 'Bold Colors',
    'earth tones': 'Earth Tones', // Fix: Match dropdown capitalization
    'earth-tones': 'Earth Tones',
    neutral: 'Neutral', // Fix: Match dropdown capitalization
    warm: 'Warm',
    cool: 'Cool',
    pastels: 'Pastels',
    monochrome: 'Monochrome',
    colorful: 'Colorful',
    dark: 'Dark',
    bright: 'Bright',
  };

  const result = mappings[normalized] || normalized;
  console.log('🎨 [COLOR NORMALIZE]', value, '→', normalized, '→', result);

  return result;
};

/**
 * Normalizes makeup type values to match dropdown options
 * Converts AI-generated values to form-compatible values
 */
const normalizeMakeupTypeValue = (value: string): string => {
  if (!value || value.trim() === '') return '';

  const normalized = value.toLowerCase().replace(/[-_]/g, ' ').trim();

  // Map common AI-generated values to dropdown values (match exact capitalization)
  const mappings: { [key: string]: string } = {
    natural: 'Natural', // Fix: Match dropdown capitalization
    minimal: 'Minimal',
    everyday: 'Everyday',
    glamour: 'Glamour', // Fix: AI generates "glamour", dropdown expects "Glamour"
    glamorous: 'Glamorous', // Fix: Match dropdown capitalization
    dramatic: 'Dramatic',
    artistic: 'Artistic',
    stage: 'Stage',
    editorial: 'Editorial',
    vintage: 'Vintage',
    'avant garde': 'Avant-garde',
    'avant-garde': 'Avant-garde',
    // QUICK FIX: Map invalid makeup types to valid ones
    colorful: 'Natural', // Backend incorrectly generates "colorful" (color palette) as makeup type
    bold: 'Dramatic', // Map color descriptors to appropriate makeup types
    bright: 'Glamorous',
    dark: 'Dramatic',
  };

  const result = mappings[normalized] || normalized;
  console.log('💄 [MAKEUP NORMALIZE]', value, '→', normalized, '→', result);

  return result;
};

/**
 * Normalizes hair color values to match dropdown options
 * Converts AI-generated values to form-compatible values
 * @deprecated Use normalizeHairColor() instead for shared vocabulary contract
 */
const normalizeHairColorValue = (value: string): string => {
  if (!value || value.trim() === '') return '';

  const normalized = value.toLowerCase().replace(/[-_]/g, ' ').trim();

  // Map common AI-generated values to dropdown values (match exact capitalization)
  const mappings: { [key: string]: string } = {
    black: 'Black', // Fix: Match dropdown capitalization
    brown: 'Brown',
    blonde: 'Blonde',
    red: 'Red',
    auburn: 'Auburn',
    gray: 'Gray',
    grey: 'Gray', // Handle spelling variation
    white: 'White',
    blue: 'Blue', // Fix: AI generates "blue", dropdown expects "Blue"
    pink: 'Pink',
    purple: 'Purple',
    green: 'Green',
    colorful: 'Colorful/Multi-color', // Fix: Match exact dropdown format
    'colorful/multi color': 'Colorful/Multi-color', // Backend sends this variant
    'colorful multi color': 'Colorful/Multi-color', // Another possible variant
    'multi color': 'Colorful/Multi-color',
    'multi colored': 'Colorful/Multi-color',
    multicolor: 'Colorful/Multi-color',
    other: 'Other',
  };

  const result = mappings[normalized] || normalized;
  console.log('💇 [HAIR COLOR NORMALIZE]', value, '→', normalized, '→', result);

  return result;
};

// =============================================================================
// 🎯 NEW SHARED VOCABULARY FUNCTIONS (Frontend-Backend Coordination)
// =============================================================================

/**
 * Normalizes hair color using shared vocabulary contract
 * Handles "multi-color", "colorful", "rainbow" → "Other"
 * COORDINATED WITH BACKEND TEAM
 */
export const normalizeHairColor = (value: string): string => {
  if (!value || value.trim() === '') return '';

  const normalized = value
    .toLowerCase()
    .replace(/[-_\s]/g, ' ')
    .trim();
  const result =
    HAIR_COLOR_VOCABULARY[normalized as keyof typeof HAIR_COLOR_VOCABULARY] ||
    value;

  console.log('🎨 [SHARED HAIR COLOR]', value, '→', normalized, '→', result);
  return result;
};

/**
 * Detects cultural context from description and maps to ethnicity
 * Handles "K-POP star" → "Korean"
 * COORDINATED WITH BACKEND TEAM
 */
export const detectCulturalContext = (description: string): string => {
  if (!description || description.trim() === '') return '';

  const desc = description.toLowerCase();

  // Check each cultural pattern
  for (const [pattern, ethnicity] of Object.entries(CULTURAL_ETHNICITY_MAP)) {
    if (desc.includes(pattern)) {
      console.log(
        '🌍 [CULTURAL CONTEXT]',
        pattern,
        '→',
        ethnicity,
        'in',
        description
      );
      return ethnicity;
    }
  }

  return '';
};

/**
 * Detects musical style from description using cultural and genre context
 * Handles "K-POP star" → ["K-Pop", "Pop"]
 * COORDINATED WITH BACKEND TEAM
 */
export const detectMusicalStyle = (description: string): string[] => {
  if (!description || description.trim() === '') return [];

  const desc = description.toLowerCase();
  const detectedStyles = new Set<string>();

  // Check each musical style pattern
  for (const [pattern, styles] of Object.entries(MUSICAL_STYLE_CONTEXT_MAP)) {
    if (desc.includes(pattern)) {
      styles.forEach(style => detectedStyles.add(style));
      console.log(
        '🎵 [MUSICAL STYLE]',
        pattern,
        '→',
        styles,
        'in',
        description
      );
    }
  }

  return Array.from(detectedStyles);
};

/**
 * Applies client-side inheritance fallback when backend inheritance fails
 * Uses baseAssetData to fill missing fields in variant
 * CRITICAL FOR VARIANT FUNCTIONALITY
 */
export const applyClientSideInheritance = (
  variantMetadata: StarMetadata,
  baseAssetData?: any
): StarMetadata => {
  if (!baseAssetData?.starMetadata && !baseAssetData?.starsMetadata) {
    console.log('⚠️ [INHERITANCE] No base asset data available for fallback');
    return variantMetadata;
  }

  const baseMetadata =
    baseAssetData.starMetadata || baseAssetData.starsMetadata || {};
  const inherited = { ...variantMetadata };

  // List of inheritable fields (fields that should copy from base if empty)
  const inheritableFields = [
    'musicalStyle',
    'ethnicity',
    'ageGroup',
    'gender',
    'hairColor',
    'hairStyle',
    'hairLength',
    'eyeColor',
    'skinTone',
    'bodyType',
    'height',
    'makeupType',
    'colorPalette',
    'outfitStyle',
    'personalityTraits',
    'performanceContext',
  ];

  inheritableFields.forEach(field => {
    const variantValue = inherited[field];
    const baseValue = extractStringValue(baseMetadata[field]);

    // Apply inheritance if variant field is empty
    if (isEmpty(variantValue) && baseValue) {
      inherited[field] = baseValue;
      console.log(
        '🔄 [INHERITANCE]',
        field,
        ':',
        baseValue,
        '(inherited from base)'
      );
    }
  });

  // Handle array fields specially (musicalStyle, personalityTraits, etc.)
  const arrayFields = [
    'musicalStyle',
    'personalityTraits',
    'performanceContext',
  ];
  arrayFields.forEach(field => {
    const variantArray = Array.isArray(variantMetadata[field])
      ? variantMetadata[field]
      : [];
    const baseArray = Array.isArray(baseMetadata[field])
      ? baseMetadata[field]
      : [];

    if (variantArray.length === 0 && baseArray.length > 0) {
      inherited[field] = baseArray.join(', '); // Convert to string for form compatibility
      console.log(
        '🔄 [INHERITANCE ARRAY]',
        field,
        ':',
        baseArray,
        '(inherited from base)'
      );
    }
  });

  return inherited;
};

/**
 * Enhanced metadata processing with vocabulary mappings and cultural context
 * Combines AI detection with client-side vocabulary normalization
 * MAIN ENTRY POINT FOR COORDINATED PROCESSING
 */
export const processMetadataWithVocabulary = (
  rawMetadata: any,
  description?: string,
  baseAssetData?: any
): StarMetadata => {
  console.log(
    '🚀 [VOCABULARY PROCESSING] Starting enhanced metadata processing'
  );

  // Step 1: Apply standard transformation
  const transformed = transformStarMetadataForFrontend(rawMetadata);

  // Step 2: Apply vocabulary normalization
  if (transformed.hairColor) {
    const hairColorString = extractStringValue(transformed.hairColor);
    if (hairColorString) {
      transformed.hairColor = normalizeHairColor(hairColorString);
    }
  }

  // Step 3: Apply cultural context detection from description
  if (description) {
    // Detect ethnicity from cultural context
    if (!transformed.ethnicity || transformed.ethnicity === '') {
      const detectedEthnicity = detectCulturalContext(description);
      if (detectedEthnicity) {
        transformed.ethnicity = detectedEthnicity;
      }
    }

    // Detect musical style from cultural context
    if (!transformed.musicalStyle || transformed.musicalStyle === '') {
      const detectedStyles = detectMusicalStyle(description);
      if (detectedStyles.length > 0) {
        transformed.musicalStyle = detectedStyles.join(', ');
      }
    }
  }

  // Step 4: Apply client-side inheritance fallback
  const final = applyClientSideInheritance(transformed, baseAssetData);

  console.log('✨ [VOCABULARY PROCESSING] Final enhanced metadata:', final);
  return final;
};

/**
 * Transforms backend starMetadata to frontend-compatible format
 * Ensures all fields are strings for form compatibility
 */
export const transformStarMetadataForFrontend = (
  backendData: StarMetadata | undefined
): StarMetadata => {
  if (!backendData) return {};

  // Enhanced field extraction with multiple fallback paths (per backend team recommendation)
  const getFieldValue = (fieldName: string) => {
    // CRITICAL FIX: Check root level first, then layerMetadata (as per FRONTEND_MUSICAL_STYLE_FIX.md)
    const value =
      (backendData as any)[fieldName] || // ✅ PRIORITY 1: Root level
      (backendData as any).layerMetadata?.[fieldName] || // ✅ PRIORITY 2: layerMetadata (MISSING FIX!)
      (backendData as any).starMetadata?.[fieldName] || // PRIORITY 3: starMetadata
      (backendData as any).aiMetadata?.starsMetadata?.[fieldName] || // PRIORITY 4: AI metadata
      (backendData as any).metadata?.starMetadata?.[fieldName] || // PRIORITY 5: Deep nested
      (backendData as any).physicalTraits?.[fieldName] || // PRIORITY 6: physicalTraits fallback
      '';

    // Enhanced debug logging for musical style specifically
    if (fieldName === 'musicalStyle') {
      console.log('🎵 [MUSICAL STYLE TRANSFORM DEBUG] Sources checked:', {
        root: (backendData as any)[fieldName],
        layerMetadata: (backendData as any).layerMetadata?.[fieldName],
        starMetadata: (backendData as any).starMetadata?.[fieldName],
        aiMetadata: (backendData as any).aiMetadata?.starsMetadata?.[fieldName],
        finalValue: value,
      });
    } else {
      console.log(`🔍 [ENHANCED EXTRACT] ${fieldName}:`, value);
    }

    return value;
  };

  console.log('🔄 [TRANSFORM] Raw backend data:', backendData);
  console.log(
    '🔍 [TRANSFORM] All backend fields:',
    Object.keys(backendData || {})
  );
  console.log('🔍 [TRANSFORM] Testing enhanced extraction...');

  const transformed = {
    // Basic information - using enhanced extraction
    starName: extractStringValue(
      getFieldValue('starName') || getFieldValue('celebrityName')
    ),
    archetype: extractStringValue(
      getFieldValue('archetype') || getFieldValue('celebrityType')
    ),
    energyLevel: extractStringValue(
      getFieldValue('energyLevel') || getFieldValue('energyProfile')
    ),
    gender: extractStringValue(getFieldValue('gender')),
    ethnicity: extractStringValue(getFieldValue('ethnicity')),
    ageGroup: extractStringValue(
      getFieldValue('ageGroup') || getFieldValue('ageRange')
    ),

    // Hair style information - using enhanced extraction
    hairStyle: extractStringValue(getFieldValue('hairStyle')),
    hairColor: normalizeHairColorValue(
      extractStringValue(getFieldValue('hairColor'))
    ),
    hairLength: extractStringValue(getFieldValue('hairLength')),

    // Physical traits - using enhanced extraction
    bodyType: extractStringValue(getFieldValue('bodyType')),
    height: extractStringValue(getFieldValue('height')),
    eyeColor: extractStringValue(getFieldValue('eyeColor')),
    skinTone: extractStringValue(getFieldValue('skinTone')),

    // Makeup information - using enhanced extraction
    makeupType: normalizeMakeupTypeValue(
      extractStringValue(
        getFieldValue('makeupType') || getFieldValue('makeupStyle')
      )
    ),
    colorPalette: normalizeColorPaletteValue(
      extractStringValue(getFieldValue('colorPalette'))
    ),

    // Career & Performance - using enhanced extraction
    performanceContext: extractStringValue(
      Array.isArray(getFieldValue('performanceContext'))
        ? getFieldValue('performanceContext').join(', ')
        : getFieldValue('performanceContext')
    ),
    musicalStyle: extractStringValue(
      Array.isArray(getFieldValue('musicalStyle'))
        ? getFieldValue('musicalStyle').join(', ')
        : getFieldValue('musicalStyle')
    ),
    personalityTraits: extractStringValue(
      Array.isArray(getFieldValue('personalityTraits'))
        ? getFieldValue('personalityTraits').join(', ')
        : getFieldValue('personalityTraits')
    ),

    // CRITICAL FIX: Add missing vibe field for Step 4 display
    vibe: extractStringValue(getFieldValue('vibe')),

    // Outfit and style information - using enhanced extraction
    outfitStyle: extractStringValue(
      getFieldValue('outfitStyle') || getFieldValue('fashionStyle')
    ),
    fashionStyle: extractStringValue(
      getFieldValue('fashionStyle') || getFieldValue('outfitStyle')
    ),
    accessories: extractStringValue(getFieldValue('accessories')),

    // Additional biographical and professional fields for comprehensive inheritance
    birthYear: extractStringValue(getFieldValue('birthYear')),
    nationality: extractStringValue(getFieldValue('nationality')),
    profession: extractStringValue(getFieldValue('profession')),
    ageRange: extractStringValue(getFieldValue('ageRange')),
    birthdayMonth: extractStringValue(getFieldValue('birthdayMonth')),
    birthdayDay: extractStringValue(getFieldValue('birthdayDay')),

    // Variant information (preserve exact values)
    assetType: getFieldValue('assetType') || backendData.assetType,
    baseStarId: getFieldValue('baseStarId') || backendData.baseStarId,
    variantName: getFieldValue('variantName') || backendData.variantName,
    variantDescription: backendData.variantDescription,

    // Arrays should remain as arrays
    compatibilityTags: Array.isArray(backendData.compatibilityTags)
      ? backendData.compatibilityTags
      : [],

    // Preserve all other fields that haven't been explicitly transformed
    ...Object.keys(backendData).reduce((acc, key) => {
      const transformedFields = [
        'starName',
        'archetype',
        'energyLevel',
        'gender',
        'ethnicity',
        'ageGroup',
        'hairStyle',
        'hairColor',
        'hairLength',
        'makeupType',
        'makeupStyle',
        'colorPalette',
        'bodyType',
        'height',
        'eyeColor',
        'skinTone',
        'performanceContext',
        'musicalStyle',
        'personalityTraits',
        'outfitStyle',
        'fashionStyle',
        'accessories',
        'birthYear',
        'nationality',
        'profession',
        'ageRange',
        'birthdayMonth',
        'birthdayDay',
        'assetType',
        'baseStarId',
        'variantName',
        'variantDescription',
        'compatibilityTags',
      ];

      if (!transformedFields.includes(key)) {
        acc[key] = backendData[key];
      }
      return acc;
    }, {} as any),
  };

  console.log('✨ [TRANSFORM] Frontend-compatible data:', transformed);
  console.log('🔍 [TRANSFORM] Final hair color:', transformed.hairColor);
  console.log('🔍 [TRANSFORM] Final makeup type:', transformed.makeupType);
  console.log('🔍 [TRANSFORM] Final color palette:', transformed.colorPalette);

  return transformed;
};

/**
 * Prepares starMetadata for sending to backend
 * Ensures consistent string format (backend will handle its own normalization)
 */
export const prepareStarMetadataForBackend = (
  formData: StarMetadata
): StarMetadata => {
  if (!formData) return {};

  console.log('📤 [PREPARE] Preparing for backend:', formData);

  // CRITICAL FIX: Only send defined values to prevent overriding backend AI generation
  const prepared: any = {};

  // Only include fields that have actual values (not empty strings or undefined)
  Object.keys(formData).forEach(key => {
    const value = formData[key];
    if (value !== undefined && value !== null && value !== '') {
      prepared[key] = value;
    }
  });

  console.log(
    '📋 [PREPARE] Backend-ready data (only defined values):',
    prepared
  );
  console.log(
    '🔍 [PREPARE] Excluded empty fields from:',
    Object.keys(formData)
  );

  return prepared;
};

/**
 * Safe string value accessor with type checking
 * Use this for any string operations to prevent TypeError
 */
export const safeStringValue = (value: any): string => {
  return extractStringValue(value);
};

/**
 * Check if a value is empty (null, undefined, empty string, empty array)
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value))
    return value.length === 0 || value.every(item => isEmpty(item));
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Normalize star metadata handling both old and new backend formats
 * This is the main function to use throughout the frontend
 */
export const normalizeStarMetadata = (rawMetadata: any): StarMetadata => {
  // Handle multiple possible locations for star metadata
  const starMetadata =
    rawMetadata?.starMetadata ||
    rawMetadata?.starsMetadata ||
    rawMetadata?.aiMetadata?.starMetadata ||
    rawMetadata?.aiMetadata?.starsMetadata ||
    rawMetadata;

  return transformStarMetadataForFrontend(starMetadata);
};

export default {
  // Legacy functions
  transformStarMetadataForFrontend,
  prepareStarMetadataForBackend,
  safeStringValue,
  isEmpty,
  normalizeStarMetadata,
  extractStringValue,

  // New shared vocabulary functions
  normalizeHairColor,
  detectCulturalContext,
  detectMusicalStyle,
  applyClientSideInheritance,
  processMetadataWithVocabulary,

  // Shared vocabulary constants
  HAIR_COLOR_VOCABULARY,
  CULTURAL_ETHNICITY_MAP,
  MUSICAL_STYLE_CONTEXT_MAP,
};
